import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader } from 'lucide-react';
import { sendAIMessage } from '../services/aiChatService';

const AIChat = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Ciao! Sono l\'assistente del Becco. Posso aiutarti con informazioni sullo stato del Becco e sugli eventi in programma. Come posso esserti utile?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll automatico all'ultimo messaggio
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus automatico sull'input al caricamento
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    // Aggiungi messaggio utente
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Prepara la cronologia per l'IA (escludi il primo messaggio di benvenuto)
      const conversationHistory = messages
        .slice(1)
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      // Invia richiesta all'IA
      const response = await sendAIMessage(inputMessage.trim(), conversationHistory);

      // Aggiungi risposta dell'IA
      const aiMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: response.timestamp
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Errore durante l\'invio del messaggio:', error);
      
      // Messaggio di errore
      const errorMessage = {
        role: 'assistant',
        content: 'Mi dispiace, si è verificato un errore. Riprova più tardi.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="flex flex-col h-screen max-h-[calc(100vh-80px)] bg-light-surface dark:bg-dark-surface p-4 sm:p-6 gap-4 sm:gap-6">
      <div className="hidden sm:flex flex-col bg-light-surface-container/95 dark:bg-dark-surface-container/95 backdrop-blur-md rounded-2xl p-6 text-center shadow-surface-container">
        <Bot className="text-light-secondary dark:text-dark-secondary mx-auto mb-3" size={24} />
        <h1 className="text-2xl sm:text-3xl font-bold text-light-on-surface dark:text-dark-on-surface mb-2">
          Chatta con il Becco
        </h1>
        <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
          L'assistente virtuale che conosce lo stato e gli eventi del Becco
        </p>
      </div>

      <div className="flex-1 overflow-y-auto bg-light-surface-container/95 dark:bg-dark-surface-container/95 backdrop-blur-md rounded-2xl p-4 sm:p-6 flex flex-col gap-4 shadow-surface-container scroll-smooth
        [&::-webkit-scrollbar]:w-2
        [&::-webkit-scrollbar-track]:bg-light-surface-variant/50 [&::-webkit-scrollbar-track]:dark:bg-dark-surface-variant/50 [&::-webkit-scrollbar-track]:rounded
        [&::-webkit-scrollbar-thumb]:bg-light-secondary/50 [&::-webkit-scrollbar-thumb]:dark:bg-dark-secondary/50 [&::-webkit-scrollbar-thumb]:rounded
        [&::-webkit-scrollbar-thumb:hover]:bg-light-secondary/70 [&::-webkit-scrollbar-thumb:hover]:dark:bg-dark-secondary/70">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 animate-fade-in ${
              message.role === 'user' ? 'flex-row-reverse' : ''
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white ${
              message.role === 'user'
                ? 'bg-gradient-to-br from-light-secondary to-light-secondary-container dark:from-dark-secondary dark:to-dark-secondary-container'
                : 'bg-gradient-to-br from-pink-400 to-rose-500'
            }`}>
              {message.role === 'user' ? (
                <User size={20} />
              ) : (
                <Bot size={20} />
              )}
            </div>
            <div className={`max-w-[70%] sm:max-w-[75%] flex flex-col gap-1 ${
              message.role === 'user' ? 'items-end' : ''
            }`}>
              <div className={`px-4 py-3 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                message.role === 'user'
                  ? 'bg-gradient-to-br from-light-secondary to-light-secondary-container dark:from-dark-secondary dark:to-dark-secondary-container text-light-on-secondary dark:text-dark-on-secondary rounded-br-sm'
                  : message.isError
                    ? 'bg-light-error/10 dark:bg-dark-error/10 border border-light-error dark:border-dark-error text-light-error dark:text-dark-error rounded-bl-sm'
                    : 'bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant rounded-bl-sm'
              }`}>
                {message.content}
              </div>
              <div className="text-[11px] text-light-on-surface-variant dark:text-dark-on-surface-variant px-2">
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-3 animate-fade-in">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-pink-400 to-rose-500 text-white">
              <Bot size={20} />
            </div>
            <div className="max-w-[70%] flex flex-col gap-1">
              <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-light-surface dark:bg-dark-surface border border-light-outline-variant dark:border-dark-outline-variant flex items-center gap-2">
                <Loader className="animate-spin text-light-secondary dark:text-dark-secondary" size={20} />
                <span className="text-light-on-surface dark:text-dark-on-surface">Sto pensando...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="bg-light-surface-container/95 dark:bg-dark-surface-container/95 backdrop-blur-md rounded-2xl p-4 sm:p-6 shadow-surface-container">
        <div className="flex gap-3 items-end">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Scrivi il tuo messaggio..."
            className="flex-1 px-4 py-3 border-2 border-light-outline dark:border-dark-outline rounded-xl text-[15px] resize-none max-h-[120px] min-h-[44px] transition-all duration-200
              bg-light-surface dark:bg-dark-surface
              text-light-on-surface dark:text-dark-on-surface
              placeholder:text-light-on-surface-variant dark:placeholder:text-dark-on-surface-variant
              focus:outline-none focus:border-light-secondary dark:focus:border-dark-secondary
              disabled:bg-light-surface-variant dark:disabled:bg-dark-surface-variant disabled:cursor-not-allowed"
            rows="1"
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="w-11 h-11 border-none rounded-xl bg-gradient-to-br from-light-secondary to-light-secondary-container dark:from-dark-secondary dark:to-dark-secondary-container text-light-on-secondary dark:text-dark-on-secondary cursor-pointer flex items-center justify-center transition-all duration-200 flex-shrink-0
              hover:enabled:-translate-y-0.5 hover:enabled:shadow-lg hover:enabled:shadow-light-secondary/40 dark:hover:enabled:shadow-dark-secondary/40
              active:enabled:translate-y-0
              disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Invia messaggio"
          >
            <Send size={20} />
          </button>
        </div>
        <div className="hidden sm:block mt-2 text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant text-center">
          Premi Invio per inviare, Shift+Invio per andare a capo
        </div>
      </div>
    </div>
  );
};

export default AIChat;
